import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';

interface BrainVisualizationProps {
    className?: string;
    style?: React.CSSProperties;
}

const BrainVisualization: React.FC<BrainVisualizationProps> = ({
                                                                   className = "",
                                                                   style = {}
                                                               }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const sceneRef = useRef<THREE.Scene | null>(null);
    const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
    const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
    const brainRef = useRef<THREE.Points | null>(null);
    const linesRef = useRef<THREE.LineSegments | null>(null);
    const animationIdRef = useRef<number | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const initializeScene = (): void => {
        if (!containerRef.current) return;

        try {
            // Initialize Three.js components
            const scene = new THREE.Scene();
            const camera = new THREE.PerspectiveCamera(
                75,
                containerRef.current.clientWidth / containerRef.current.clientHeight,
                0.1,
                1000
            );
            const renderer = new THREE.WebGLRenderer({
                alpha: true,
                antialias: true
            });

            renderer.setSize(
                containerRef.current.clientWidth,
                containerRef.current.clientHeight
            );
            containerRef.current.appendChild(renderer.domElement);

            camera.position.z = 8;

            // Store references
            sceneRef.current = scene;
            cameraRef.current = camera;
            rendererRef.current = renderer;

            setError(null);
        } catch (err) {
            setError('Failed to initialize 3D scene');
            console.error('Scene initialization error:', err);
        }
    };

    const createBrainVisualization = (): void => {
        if (!sceneRef.current) return;

        try {
            // Create brain-like structure with points
            const geometry = new THREE.SphereGeometry(4, 32, 32);
            const points: number[] = [];
            const colors: number[] = [];

            const positionAttribute = geometry.attributes.position as THREE.BufferAttribute;

            for (let i = 0; i < positionAttribute.count; i++) {
                const x = positionAttribute.getX(i);
                const y = positionAttribute.getY(i);
                const z = positionAttribute.getZ(i);

                // Distort sphere to look more brain-like
                const noise = Math.sin(x * 2) * 0.3 + Math.cos(y * 3) * 0.2;
                points.push(x + noise, y + noise * 0.5, z + noise * 0.7);

                // Orange to white gradient
                const intensity = Math.random();
                colors.push(1, 0.5 + intensity * 0.5, intensity);
            }

            const pointsGeometry = new THREE.BufferGeometry();
            pointsGeometry.setAttribute('position', new THREE.Float32BufferAttribute(points, 3));
            pointsGeometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

            const pointsMaterial = new THREE.PointsMaterial({
                size: 0.05,
                vertexColors: true,
                blending: THREE.AdditiveBlending,
                transparent: true,
                opacity: 0.8
            });

            const brain = new THREE.Points(pointsGeometry, pointsMaterial);
            sceneRef.current.add(brain);
            brainRef.current = brain;

            // Add connecting lines
            createConnectingLines(points);

            // Dispose of temporary geometry
            geometry.dispose();
        } catch (err) {
            setError('Failed to create brain visualization');
            console.error('Brain creation error:', err);
        }
    };

    const createConnectingLines = (points: number[]): void => {
        if (!sceneRef.current) return;

        const linesGeometry = new THREE.BufferGeometry();
        const linePositions: number[] = [];
        const lineColors: number[] = [];
        const numberOfLines = 200;

        for (let i = 0; i < numberOfLines; i++) {
            const i1 = Math.floor(Math.random() * (points.length / 3));
            const i2 = Math.floor(Math.random() * (points.length / 3));

            linePositions.push(
                points[i1 * 3],
                points[i1 * 3 + 1],
                points[i1 * 3 + 2],
                points[i2 * 3],
                points[i2 * 3 + 1],
                points[i2 * 3 + 2]
            );

            const intensity = Math.random() * 0.5;
            lineColors.push(
                1, 0.5, 0, intensity,
                1, 0.5, 0, intensity
            );
        }

        linesGeometry.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));
        linesGeometry.setAttribute('color', new THREE.Float32BufferAttribute(lineColors, 4));

        const linesMaterial = new THREE.LineBasicMaterial({
            vertexColors: true,
            transparent: true,
            opacity: 0.3,
            blending: THREE.AdditiveBlending
        });

        const lines = new THREE.LineSegments(linesGeometry, linesMaterial);
        sceneRef.current.add(lines);
        linesRef.current = lines;
    };

    const animate = (): void => {
        if (!rendererRef.current || !sceneRef.current || !cameraRef.current) return;

        animationIdRef.current = requestAnimationFrame(animate);

        // Rotate brain and lines
        if (brainRef.current) {
            brainRef.current.rotation.x += 0.001;
            brainRef.current.rotation.y += 0.002;
        }

        if (linesRef.current) {
            linesRef.current.rotation.x += 0.001;
            linesRef.current.rotation.y += 0.002;
        }

        rendererRef.current.render(sceneRef.current, cameraRef.current);
    };

    const handleResize = (): void => {
        if (!containerRef.current || !cameraRef.current || !rendererRef.current) return;

        cameraRef.current.aspect = containerRef.current.clientWidth / containerRef.current.clientHeight;
        cameraRef.current.updateProjectionMatrix();
        rendererRef.current.setSize(
            containerRef.current.clientWidth,
            containerRef.current.clientHeight
        );
    };

    const cleanup = (): void => {
        // Cancel animation
        if (animationIdRef.current) {
            cancelAnimationFrame(animationIdRef.current);
            animationIdRef.current = null;
        }

        // Remove event listeners
        window.removeEventListener('resize', handleResize);

        // Dispose geometries and materials
        if (brainRef.current) {
            brainRef.current.geometry.dispose();
            (brainRef.current.material as THREE.Material).dispose();
        }

        if (linesRef.current) {
            linesRef.current.geometry.dispose();
            (linesRef.current.material as THREE.Material).dispose();
        }

        // Remove renderer from DOM
        if (rendererRef.current && containerRef.current) {
            if (rendererRef.current.domElement.parentNode === containerRef.current) {
                containerRef.current.removeChild(rendererRef.current.domElement);
            }
            rendererRef.current.dispose();
        }

        // Clear references
        sceneRef.current = null;
        cameraRef.current = null;
        rendererRef.current = null;
        brainRef.current = null;
        linesRef.current = null;
    };

    // Initialize visualization
    useEffect(() => {
        setIsLoading(true);

        const timer = setTimeout(() => {
            initializeScene();
            createBrainVisualization();
            animate();

            // Add resize listener
            window.addEventListener('resize', handleResize);

            setIsLoading(false);
        }, 100); // Small delay to ensure DOM is ready

        // Cleanup on unmount
        return () => {
            clearTimeout(timer);
            cleanup();
        };
    }, []);

    return (
        <div
            className={`relative ${className}`}
            style={{
                width: '100%',
                height: '100%',
                minHeight: '400px',
                ...style
            }}
        >
            <div
                ref={containerRef}
                className="w-full h-full"
                style={{
                    background: 'transparent',
                    overflow: 'hidden'
                }}
            />

            {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="text-white text-lg">Loading 3D Visualization...</div>
                </div>
            )}

            {error && (
                <div className="absolute inset-0 flex items-center justify-center bg-red-500 bg-opacity-50">
                    <div className="text-white text-lg">{error}</div>
                </div>
            )}
        </div>
    );
};

export default BrainVisualization;